import React, { PureComponent } from "react";
import { Field, reduxForm, SubmissionError } from "redux-form";
import { withGoogleReCaptcha } from "react-google-recaptcha-v3";
import Textarea from "../inputs/Textarea";
import TextInput from "../inputs/TextInput";
import { Button } from "../util/Clickable";
import * as normalize from "../util/Normalizer";
import { getPureMessage, IntlText } from "../util/Text";
import * as validate from "../util/Validator";

class ContactForm extends PureComponent {
  state = {
    loading: false,
    recaptchaReady: false,
  };

  // フォームが表示された時刻を記録（ボット対策）
  formLoadTime = Date.now();

  componentDidMount() {
    // reCAPTCHAの読み込み状態を確認
    this.checkRecaptchaInterval = setInterval(() => {
      if (this.props.googleReCaptchaProps?.executeRecaptcha) {
        this.setState({ recaptchaReady: true });
        clearInterval(this.checkRecaptchaInterval);
      }
    }, 100);

    // 10秒後にタイムアウト
    this.recaptchaTimeout = setTimeout(() => {
      clearInterval(this.checkRecaptchaInterval);
      if (!this.state.recaptchaReady) {
        console.error("reCAPTCHA loading timeout");
      }
    }, 10000);
  }

  componentWillUnmount() {
    if (this.checkRecaptchaInterval) {
      clearInterval(this.checkRecaptchaInterval);
    }
    if (this.recaptchaTimeout) {
      clearTimeout(this.recaptchaTimeout);
    }
  }

  onSubmit = async (values) => {
    this.setState({
      loading: true,
    });

    try {
      // ハニーポットチェック - ボットが隠しフィールドに入力していないか確認
      // ただし、フォーム表示から3秒以内の送信の場合のみチェック（自動補完を除外）
      const timeSinceLoad = Date.now() - this.formLoadTime;
      const isLikelyBot = timeSinceLoad < 3000; // 3秒以内は怪しい

      if ((values.fake_field || values.fake_field_2) && isLikelyBot) {
        this.setState({
          loading: false,
        });
        throw new SubmissionError({
          _error: "スパム検出: 不正な送信です。",
        });
      }

      // reCAPTCHA トークンを取得
      const token = await this.props.googleReCaptchaProps.executeRecaptcha(
        "contact_form",
      );
      const valuesWithToken = { ...values, recaptcha_token: token };

      return this.props.onSubmit(valuesWithToken).then(({ value, action }) => {
        if (value.status < 300) {
          this.setState(
            {
              loading: false,
            },
            () => {
              this.props.onSuccess &&
                this.props.onSuccess(value.data, value.response);
            },
          );
        } else {
          this.setState({
            loading: false,
          });
          const errors = Object.keys(value.errors).reduce((dic, key) => {
            dic[key] = Array.isArray(value.errors[key])
              ? value.errors[key].join("\n")
              : null;
            return dic;
          }, {});
          throw new SubmissionError(errors);
        }
      });
    } catch (error) {
      this.setState({
        loading: false,
      });
      throw error;
    }
  };

  render = () => {
    return (
      <form
        onSubmit={this.props.handleSubmit(this.onSubmit)}
        action={this.props.action}
        method="post"
      >
        {/* ハニーポット - ボット対策用の隠しフィールド */}
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            opacity: 0,
            pointerEvents: "none",
          }}
          aria-hidden="true"
          tabIndex="-1"
        >
          <Field
            name="fake_field"
            type="text"
            component={TextInput}
            labelId="forms.attr.website"
            tabIndex="-1"
            autoComplete="off"
          />
          <Field
            name="fake_field_2"
            type="text"
            component={TextInput}
            labelId="forms.attr.company"
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        <div className="uk-margin-small">
          <div className="uk-margin-ssmall">
            <Field
              name="name"
              type="text"
              component={TextInput}
              labelId="forms.attr.contact.name"
              validate={[validate.required]}
              required
            />
          </div>
          <div className="uk-margin-ssmall">
            <Field
              name="email"
              type="email"
              component={TextInput}
              labelId="forms.attr.email"
              validate={[validate.required, validate.email]}
              required
            />
          </div>
          <div className="uk-margin-ssmall">
            <Field
              name="tel"
              type="tel"
              component={TextInput}
              labelId="forms.attr.tel"
              validate={[validate.required, validate.tel]}
              required
              normalize={normalize.numberOnly}
            />
          </div>
          <div className="uk-margin-ssmall">
            <Field
              name="description"
              component={Textarea}
              labelId="forms.attr.contact.description"
              validate={[validate.required]}
              placeholder={getPureMessage(
                "forms.attr.contact.description_placeholder",
              )}
              ga={{ category: "input", action: "contact__description" }}
              required
            />
          </div>
        </div>
        {(() => {
          if (this.props.error) {
            return (
              <div className="uk-margin-small uk-text-danger uk-text-center@s">
                <span>{this.props.error}</span>
              </div>
            );
          }
        })()}
        <div className="uk-margin-small uk-text-center">
          <Button
            className="uk-button uk-button-default"
            type="submit"
            disabled={this.props.invalid || !this.state.recaptchaReady}
            ga={{ category: this.props.form, action: "submit" }}
            loading={this.state.loading ? 1 : 0}
          >
            <IntlText id="forms.submit" />
          </Button>
          {!this.state.recaptchaReady && (
            <div className="uk-margin-small uk-text-muted uk-text-small">
              <span>
                セキュリティチェックを読み込み中...
                <br />
                長時間表示される場合はページをリロードしてください。
              </span>
            </div>
          )}
        </div>

        <Field name="gallery_id" type="hidden" component={TextInput} noError />
      </form>
    );
  };
}

export default reduxForm({
  form: "contact-form",
  enableReinitialize: true,
  withRef: true,
})(withGoogleReCaptcha(ContactForm));
